// LOGS DE WEBHOOK: a auditoria das integrações EXTERNAS que entram no Disk.
// Espelha a feature `webhook-logs` do back (/v1/webhook-logs): toda requisição
// de entrada dos webhooks de integração (HubSpot e futuros), do payload que
// chega até a resposta que devolvemos. É a ferramenta de debug de quem cuida da
// integração — leitura restrita a diretor/gerente (403 para vendedor).
//
// O escopo é do back e vale só para a org ativa: log sem org resolvida (payload
// recusado antes de identificar a marca) não é de ninguém e não aparece — antes
// vazava para a lista de TODAS as orgs. Segredos (x-api-key / authorization /
// cookie) já vêm REDIGIDOS do back; datas chegam em ISO.
//
// Fica sozinho, e não junto das outras auditorias que saíram de api.ts na mesma
// leva (./click-to-call-audit.js e ./predictive.js), porque "auditoria" ali é só
// a palavra: o assunto aqui é HTTP de terceiro entrando, lá é o funil de uma
// ligação. Nenhum tipo é compartilhado e nenhum dos três importaria o outro —
// juntá-los daria um arquivo de 300 linhas com três vocabulários.
import { api, toApiError } from "./client.js";

export const WEBHOOK_OUTCOMES = ["success", "rejected", "error"] as const;
export type WebhookOutcome = (typeof WEBHOOK_OUTCOMES)[number];

// Rótulos em português para exibição (UI toda em pt-br).
export const WEBHOOK_OUTCOME_LABELS: Record<WebhookOutcome, string> = {
  success: "Sucesso",
  rejected: "Rejeitada",
  error: "Erro",
};

// Campos ordenáveis da listagem (espelham a whitelist do back).
export const WEBHOOK_SORT_FIELDS = ["createdAt", "statusCode", "durationMs"] as const;
export type WebhookSortField = (typeof WEBHOOK_SORT_FIELDS)[number];
export type WebhookSortDir = "asc" | "desc";

// Projeção leve para a tabela (sem os corpos jsonb).
export interface WebhookLogSummary {
  id: string;
  source: string;
  event: string | null;
  method: string;
  path: string;
  organizationId: string | null;
  outcome: WebhookOutcome;
  statusCode: number;
  errorMessage: string | null;
  durationMs: number;
  remoteIp: string | null;
  createdAt: string;
}

// Detalhe completo (GET /v1/webhook-logs/:id): headers/payload/resposta.
export interface WebhookLogDetail extends WebhookLogSummary {
  requestHeaders: unknown;
  requestBody: unknown;
  responseBody: unknown;
}

export interface WebhookLogsResponse {
  logs: WebhookLogSummary[];
  total: number;
  limit: number;
  offset: number;
  viewerRole: string;
  sources: string[];
}

export interface WebhookLogsQuery {
  source?: string;
  outcome?: WebhookOutcome;
  q?: string;
  from?: string;
  to?: string;
  sortBy?: WebhookSortField;
  sortDir?: WebhookSortDir;
  limit?: number;
  offset?: number;
}

// GET /v1/webhook-logs — página de logs da org ativa.
export async function getWebhookLogs(params: WebhookLogsQuery = {}): Promise<WebhookLogsResponse> {
  const { data, error } = await api<WebhookLogsResponse>("/v1/webhook-logs", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/webhook-logs", error);
  return data;
}

// GET /v1/webhook-logs/:id — detalhe com payload de entrada e resposta.
export async function getWebhookLog(id: string): Promise<WebhookLogDetail> {
  const { data, error } = await api<WebhookLogDetail>(`/v1/webhook-logs/${id}`);
  if (error) throw toApiError(`GET /v1/webhook-logs/${id}`, error);
  return data;
}
